import { VUEX_GENERAL, VUEX_REBATES } from '@/constants/VuexConstants';
import PolicyStatus from '@general/js/enums/PolicyStatusEnum';
import { mapGetters, mapMutations } from 'vuex';
import { RebateEventTypesFlexCodes, RebateEventStatusFlexCodes, InsurableTypeEnum } from '@/enums';
import { MyCircumstances, MyCircumstancesDisplay } from '@general/js/enums/MyCircumstancesEnum';
import { RebateTierCode, RebateTierDisplayWithoutIncomeBrackets } from '@/enums/RebateEnums';
import { isEmptyString, formatAsCurrency, mapPaymentFrequencyFromRecurringToSingleton } from '@/utils';
import { REBATES_MEMBER_PORTAL } from '@/router/routeNames';
import { setNavigationState } from '@/router/navigationState';
import { getCurrentDate } from '@general/js/utils/dateTimeUtils';
import RebateServices from '@/services/MyCover/Rebates';

export default {
  data() {
    return {
      myCircumstancesOptions: [
        { value: MyCircumstances.SINGLE, displayValue: MyCircumstancesDisplay.SINGLE },
        { value: MyCircumstances.OTHER, displayValue: MyCircumstancesDisplay.OTHER },
      ],
      incomeTierSingleOptions: [],
      incomeTierFamilyOptions: [],
      currentPremium: null,
      newEstimatedPremium: null,
    };
  },

  watch: {
    refData: {
      handler: 'initializeRebateTierOptions',
    },
  },

  updated() {
    this.bindRefDataLinks();
  },

  methods: {
    ...mapMutations('Rebates', {
      updateRebateHistory: VUEX_REBATES.MUTATIONS.updateRebateHistory,
      updateMembershipDetails: VUEX_REBATES.MUTATIONS.updateMembershipDetails,
      updateSubmitPolicyInProcess: VUEX_REBATES.MUTATIONS.updateSubmitPolicyInProcess,
    }),
    formatAsCurrency,
    mapPaymentFrequencyFromRecurringToSingleton,

    // This function binds navigation click for buttons/links passed in via ref data
    bindRefDataLinks() {
      const ID_SELECTORS = {
        addRebate: '#addRebate',
        updateRebate: '#updateRebate',
        withdrawRebate: '#withdrawRebate',
        viewRebateHistory: '#viewRebateHistory',
      };

      if (document.querySelectorAll(ID_SELECTORS.addRebate).length > 0) {
        const elements = document.querySelectorAll(ID_SELECTORS.addRebate);

        elements.forEach((e) => {
          e.addEventListener('click', (event) => {
            event.preventDefault(); // Prevent default action of the link so that # isn't added to the URL and browser history isn't polluted
            return this.navigateToPage(REBATES_MEMBER_PORTAL.ADD_REBATE);
          });
        });
      }

      if (document.querySelectorAll(ID_SELECTORS.updateRebate).length > 0) {
        const elements = document.querySelectorAll(ID_SELECTORS.updateRebate);

        elements.forEach((e) => {
          e.addEventListener('click', (event) => {
            event.preventDefault();
            return this.navigateToPage(REBATES_MEMBER_PORTAL.UPDATE_REBATE);
          });
        });
      }

      if (document.querySelectorAll(ID_SELECTORS.withdrawRebate).length > 0) {
        const elements = document.querySelectorAll(ID_SELECTORS.withdrawRebate);

        elements.forEach((e) => {
          e.addEventListener('click', (event) => {
            event.preventDefault();
            return this.navigateToPage(REBATES_MEMBER_PORTAL.WITHDRAW_REBATE);
          });
        });
      }

      if (document.querySelectorAll(ID_SELECTORS.viewRebateHistory).length > 0) {
        const elements = document.querySelectorAll(ID_SELECTORS.viewRebateHistory);

        elements.forEach((e) => {
          e.addEventListener('click', (event) => {
            event.preventDefault();
            return this.navigateToPage(REBATES_MEMBER_PORTAL.REBATE_TIER_HISTORY);
          });
        });
      }
    },

    navigateToPage(to, param = {}) {
      setNavigationState({
        isFromLandingPage: true,
        ...param,
      });
      this.$router.push({
        name: to,
      });
    },

    updateStep(step) {
      this.currentPage = step;
    },

    getRebateTierList(circumstances) {
      const rebateTierOptions = this.rebateTierOptions.find((item) => {
        return item.myCircumstance === circumstances;
      })?.rebateTierOptions;

      const list = [];
      rebateTierOptions.forEach((op) => {
        if (op.dhl_code.toUpperCase() === RebateTierCode.NONE) {
          list.push({
            value: op.dhl_code,
            name: `${op.dhl_name}`,
          });
        } else {
          list.push({
            value: op.dhl_code,
            name: `${RebateTierDisplayWithoutIncomeBrackets[op.dhl_code.toUpperCase()]} - ${op.dhl_name}`,
          });
        }
      });
      return list;
    },

    getRebateTierInitialSelect(rebateTierVal, circumstances) {
      if (isEmptyString(rebateTierVal)) {
        return undefined;
      }

      const rebateTierInfo = this.getRebateTierList(circumstances).filter((tierDetails) => {
        return tierDetails.value.toUpperCase() === rebateTierVal.toUpperCase();
      });

      return rebateTierInfo[0]?.name;
    },

    initializeRebateTierOptions(refData) {
      if (refData) {
        this.incomeTierSingleOptions = refData.incomeTierSingleOptions ?? [];
        this.incomeTierFamilyOptions = refData.incomeTierFamilyOptions ?? [];
      }
    },

    async fetchNewPremium(rebateTier, apiStatus) {
      apiStatus.loading = true;
      apiStatus.fail = false;
      try {
        const payload = {
          rebateTier,
          currentDate: getCurrentDate(),
        };

        const response = await RebateServices.estimateNewPremium(this.memberNumber, payload);
        this.currentPremium = response.currentPremium;
        this.newEstimatedPremium = response.newPremium;
        const memberDetailsResponse = await RebateServices.retrieveMembershipDetails(this.memberNumber);
        this.updateMembershipDetails(memberDetailsResponse);
      } catch (error) {
        apiStatus.fail = true;
        logger.log('Error fetching new premium', error);
      } finally {
        apiStatus.loading = false;
      }
    },

    async fetchMembershipDetails() {
      this.apiStatus.submit.loading = true;
      this.apiStatus.submit.fail = false;
      try {
        const response = await RebateServices.retrieveMembershipDetails(this.memberNumber);
        this.updateMembershipDetails(response);
      } catch (error) {
        this.apiStatus.submit.fail = true;
        logger.log('Error fetching membership details', error);
      } finally {
        this.apiStatus.submit.loading = false;
      }
    },
  },

  computed: {
    ...mapGetters({
      memberNumber: VUEX_GENERAL.GETTERS.getCurrentPolicyNumber,
      membershipInfo: VUEX_GENERAL.GETTERS.getMembershipInfo,
      currentMemberDetails: VUEX_GENERAL.GETTERS.getCurrentMemberDetails,
      members: VUEX_GENERAL.GETTERS.getMembers,
      accessLevel: VUEX_GENERAL.GETTERS.getAccessLevel,
    }),

    ...mapGetters('Rebates', {
      rebateHistory: VUEX_REBATES.GETTERS.getRebateHistory,
      membershipDetails: VUEX_REBATES.GETTERS.getMembershipDetails,
      submitPolicyInProcess: VUEX_REBATES.GETTERS.getSubmitPolicyInProcess,
    }),

    navLinks() {
      return Object.values(this.pages).map((page) => {
        return {
          linkTitle: page.pageTitle,
          pages: [page.pageNumber],
          disabled: page.isInvalid,
        };
      });
    },

    isContributorOrDependant() {
      const currentMember = this.rebateHistory.members?.find((member) => {
        return member.personCode === this.currentMemberDetails.personCode;
      });

      if (currentMember) {
        return [InsurableTypeEnum.DEPENDANT, InsurableTypeEnum.NON_STUDENT, InsurableTypeEnum.STUDENT, InsurableTypeEnum.CONTRIBUTOR].includes(currentMember?.insurableType);
      }
      return false;
    },

    rebateTierOptions() {
      return [
        {
          myCircumstance: MyCircumstances.SINGLE,
          rebateTierOptions:
            this.refData?.incomeTierSingleOptions?.map((opt) => {
              return {
                dhl_name: opt.display,
                dhl_code: opt.returnValue,
              };
            }) ?? [],
        },
        {
          myCircumstance: MyCircumstances.OTHER,
          rebateTierOptions:
            this.refData?.incomeTierFamilyOptions?.map((opt) => {
              return {
                dhl_name: opt.display,
                dhl_code: opt.returnValue,
              };
            }) ?? [],
        },
      ];
    },

    isRebateUnprocessed() {
      // Rebate is unprocessed if the latest event status is either NEW or SENT
      const latestRebateEvent = this.rebateHistory?.rebateEventHistory?.[0];
      return [RebateEventStatusFlexCodes.NEW, RebateEventStatusFlexCodes.SENT].includes(latestRebateEvent?.status);
    },

    isRebateRejected() {
      // Rebate is rejected if the latest event status is REJECTED
      const latestRebateEvent = this.rebateHistory?.rebateEventHistory?.[0];
      return latestRebateEvent?.status === RebateEventStatusFlexCodes.REJECTED;
    },

    isPolicySuspended() {
      return this.membershipInfo?.policyStatus.toLowerCase() === PolicyStatus.SUSPENDED.toLowerCase();
    },

    isViewRebateHistoryAllowed() {
      // "View rebate tier history" button will be displayed as long as there is any entry in the rebate history endpoint, regardless of active status. (rebateEventHistory[])
      return this.rebateHistory?.rebateEventHistory?.length > 0;
    },

    isAddARebateAllowed() {
      //   Add a rebate button is displayed when one of:
      // a policy does NOT have any rebateTierHistory
      const hasNoRebateTierHistory = this.rebateHistory?.rebateTierHistory?.length === 0;

      // a policy has the latest event as W and status of WITHDRAWAL ACCEPTED
      const latestRebateEvent = this.rebateHistory?.rebateEventHistory?.[0];
      const hasLatestEventAsW = latestRebateEvent?.rebateEventType === RebateEventTypesFlexCodes.APPLICANT_WITHDRAWAL;
      const hasLatestEventStatus = RebateEventStatusFlexCodes.WITHDRAWAL_ACCEPTED === latestRebateEvent?.status;

      return (hasNoRebateTierHistory || (hasLatestEventAsW && hasLatestEventStatus)) && !this.isPolicySuspended;
    },

    isUpdateRebateAllowed() {
      // "Update your rebate tier" button is displayed when:
      // a policy has the latest event as R and the status is REGISTRATION ACCEPTED
      const latestRebateEvent = this.rebateHistory?.rebateEventHistory?.[0];
      const hasLatestEventAsR = latestRebateEvent?.rebateEventType === RebateEventTypesFlexCodes.APPLICANT_REGISTRATION;
      const hasLatestEventStatus = latestRebateEvent?.status === RebateEventStatusFlexCodes.REGISTRATION_ACCEPTED;

      return hasLatestEventAsR && hasLatestEventStatus && !this.isPolicySuspended;
    },

    isWithdrawRebateAllowed() {
      // "Withdraw your rebate" button is displayed when a policy has the latest event as R and the status is REGISTRATION ACCEPTED
      const latestRebateEvent = this.rebateHistory?.rebateEventHistory?.[0];
      const hasLatestEventAsR = latestRebateEvent?.rebateEventType === RebateEventTypesFlexCodes.APPLICANT_REGISTRATION;
      const hasLatestEventStatus = latestRebateEvent?.status === RebateEventStatusFlexCodes.REGISTRATION_ACCEPTED;

      return hasLatestEventAsR && hasLatestEventStatus && !this.isPolicySuspended;
    },

    doesPolicyHaveAnActiveRebate() {
      // a policy has the latest event as W and status of NEW  SENT or WITHDRAWAL ACCEPTED
      // OR
      // a policy has the latest event as R and status of REJECTED

      const latestRebateEvent = this.rebateHistory?.rebateEventHistory?.[0];
      const hasLatestEventAsW = latestRebateEvent?.rebateEventType === RebateEventTypesFlexCodes.APPLICANT_WITHDRAWAL;
      const hasLatestEventStatus = [RebateEventStatusFlexCodes.NEW, RebateEventStatusFlexCodes.SENT, RebateEventStatusFlexCodes.WITHDRAWAL_ACCEPTED].includes(
        latestRebateEvent?.status,
      );

      const hasLatestEventAsR = latestRebateEvent?.rebateEventType === RebateEventTypesFlexCodes.APPLICANT_REGISTRATION;
      const hasLatestEventRejected = latestRebateEvent?.status === RebateEventStatusFlexCodes.REJECTED;

      return (hasLatestEventAsW && hasLatestEventStatus) || (hasLatestEventAsR && hasLatestEventRejected);
    },
  },
};
